import { useEffect, type RefObject } from "react";

/**
 * Toggles `data-scrolled` / `data-scrolled-end` on the table's scroll container
 * so CSS in `table.module.css` can render boundary shadows on sticky columns.
 */
export function useScrollShadows(tableRef: RefObject<HTMLTableElement | null>) {
  useEffect(() => {
    const container = tableRef.current?.parentElement;
    if (!container || !tableRef.current) return;
    return trackScrollEdges(container, container, [tableRef.current]);
  }, [tableRef]);
}

/**
 * The same edge flags for a horizontally scrolling row that isn't a table,
 * set on the row's parent rather than the row itself: shadows drawn on the
 * scroller would scroll away with its content, the parent stays put. The
 * children present at mount are watched for size changes, so one that grows
 * after mount (options loaded from the API) updates the flags too. A child
 * added or removed later isn't watched — wrap conditional actions in one
 * element that is always there.
 */
export function useScrollEdges(scrollerRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const scroller = scrollerRef.current;
    const target = scroller?.parentElement;
    if (!scroller || !target) return;
    return trackScrollEdges(scroller, target, Array.from(scroller.children));
  }, [scrollerRef]);
}

/**
 * Flags on `target` whether `container` is scrolled away from its start
 * (`data-scrolled`) and whether there is more to scroll to (`data-scrolled-end`).
 * Returns the cleanup.
 */
function trackScrollEdges(
  container: HTMLElement,
  target: HTMLElement,
  content: Element[],
): () => void {
  // Cached so the scroll handler below reads only `scrollLeft`: `scrollWidth`
  // and `clientWidth` flush layout, and on a wide table that's a synchronous
  // layout per scroll event for values that only change on resize.
  let maxScroll = 0;

  const measure = () => {
    maxScroll = container.scrollWidth - container.clientWidth;
  };

  const update = () => {
    target.toggleAttribute("data-scrolled", container.scrollLeft > 0);
    target.toggleAttribute(
      "data-scrolled-end",
      container.scrollLeft < maxScroll - 1,
    );
  };

  measure();
  update();
  container.addEventListener("scroll", update);

  // Recompute when the content or viewport width changes (async data load,
  // column visibility, window resize) — those don't fire `scroll` events, so
  // without this the boundary shadows stay stale until the user scrolls.
  const observer = new ResizeObserver(() => {
    measure();
    update();
  });
  observer.observe(container);
  for (const element of content) observer.observe(element);

  return () => {
    container.removeEventListener("scroll", update);
    observer.disconnect();
  };
}
